import { useState, useRef, useImperativeHandle, forwardRef, KeyboardEvent, useEffect } from 'react';
import { FlaskConical, X, Loader2, AlertCircle, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  useSimulateAppointmentType,
  useProviderSelectionSettings,
  useUpdateProviderSelectionSettings,
} from '@/hooks/use-appointment-type-matching';
import { AppointmentTypeResults } from '@/components/custom/appointment-type-results';
import { ProviderPicker } from '@/components/custom/provider-picker';
import { LocationPicker } from '@/components/custom/location-picker';
import type { Prioritization, MatchScoreTieBreak } from '@vagusx/clients';
import { DEFAULT_PRIORITIZATION } from '@vagusx/clients';

// =============================================================================
// Types
// =============================================================================

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// =============================================================================
// Tag Input Component
// =============================================================================

export interface TagInputHandle {
  /** Flush any uncommitted text and return it (uppercased), or null if empty. */
  flush: () => string | null;
}

interface TagInputProps {
  label: string;
  tags: string[];
  onAdd: (tag: string) => void;
  onRemove: (tag: string) => void;
  placeholder: string;
}

const TagInput = forwardRef<TagInputHandle, TagInputProps>(function TagInput(
  { label, tags, onAdd, onRemove, placeholder },
  ref
) {
  const [inputValue, setInputValue] = useState('');

  function commitValue(): string | null {
    if (inputValue.trim()) {
      const value = inputValue.trim().toUpperCase();
      if (!tags.includes(value)) {
        onAdd(value);
      }
      setInputValue('');
      return value;
    }
    return null;
  }

  useImperativeHandle(ref, () => ({ flush: commitValue }));

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if ((e.key === 'Enter' || e.key === ',') && inputValue.trim()) {
      e.preventDefault();
      commitValue();
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      onRemove(tags[tags.length - 1]!);
    }
  }

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-1.5 min-h-9 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus-within:ring-1 focus-within:ring-ring">
        {tags.map((tag) => (
          <Badge key={tag} variant="secondary" className="gap-1 py-0.5">
            {tag}
            <button
              type="button"
              onClick={() => onRemove(tag)}
              className="ml-0.5 rounded-sm opacity-70 hover:opacity-100"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commitValue}
          placeholder={tags.length === 0 ? placeholder : ''}
          className="h-5 flex-1 min-w-20 border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
        />
      </div>
      <p className="text-xs text-muted-foreground">Press Enter or comma to add</p>
    </div>
  );
});

// =============================================================================
// Results Panel
// =============================================================================

// =============================================================================
// Algorithm Weights Panel
// =============================================================================

interface WeightsPanelProps {
  fairnessWeight: number;
  onFairnessWeightChange: (weight: number) => void;
  tieBreak: MatchScoreTieBreak;
  onTieBreakChange: (mode: MatchScoreTieBreak) => void;
  onSaveDefault: () => void;
  isSaving: boolean;
  isDirty: boolean;
}

/**
 * Tuning controls for the ranking algorithm.
 *
 * Changes here ride along as PER-RUN overrides and are never persisted until
 * "Save as org default" is pressed — so moving a weight to see what happens
 * costs nothing and cannot affect live routing.
 *
 * Fairness is the only free input: rating is derived as `1 − fairness`. The two
 * weights must sum to 1 (the API rejects anything else), and deriving it makes
 * that impossible to violate rather than merely validated after the fact.
 */
function WeightsPanel({
  fairnessWeight,
  onFairnessWeightChange,
  tieBreak,
  onTieBreakChange,
  onSaveDefault,
  isSaving,
  isDirty,
}: WeightsPanelProps) {
  const [open, setOpen] = useState(false);
  const providerWeightShare = Math.round((1 - fairnessWeight) * 100) / 100;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="space-y-2">
      <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground">
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
        Algorithm weights
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="fairness-weight">Availability weight</Label>
          <Input
            id="fairness-weight"
            type="number"
            min={0}
            max={1}
            step={0.05}
            value={fairnessWeight}
            onChange={(e) => {
              const next = Number(e.target.value);
              if (Number.isNaN(next)) return;
              onFairnessWeightChange(Math.min(1, Math.max(0, next)));
            }}
          />
          <p className="text-xs text-muted-foreground">
            Provider-weight share is {providerWeightShare} — the two always add up to 1.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label>Match-score tie-break</Label>
          <Select value={tieBreak} onValueChange={(v) => onTieBreakChange(v as MatchScoreTieBreak)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="after">After priority</SelectItem>
              <SelectItem value="before">Before priority</SelectItem>
              <SelectItem value="off">Ignore</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="outline"
          size="sm"
          className="w-full"
          disabled={!isDirty || isSaving}
          onClick={onSaveDefault}
        >
          {isSaving ? 'Saving...' : 'Save as org default'}
        </Button>
      </CollapsibleContent>
    </Collapsible>
  );
}

// =============================================================================
// Main Dialog
// =============================================================================

export function AppointmentTypeSimulatorDialog({ open, onOpenChange }: Props) {
  const [cptCodes, setCptCodes] = useState<string[]>([]);
  const [icdCodes, setIcdCodes] = useState<string[]>([]);
  const [patientType, setPatientType] = useState<'new' | 'existing' | 'any'>('any');
  const [isUrgent, setIsUrgent] = useState(false);
  const [providerId, setProviderId] = useState('');
  const [locationId, setLocationId] = useState('');
  const cptRef = useRef<TagInputHandle>(null);
  const icdRef = useRef<TagInputHandle>(null);

  const simulate = useSimulateAppointmentType();
  const savedSettings = useProviderSelectionSettings();
  const saveSettings = useUpdateProviderSelectionSettings();

  // Tuning state. Seeded from the org's saved policy once it loads, then owned
  // locally — a re-fetch must not yank a weight out from under someone mid-tune.
  const [fairnessWeight, setFairnessWeight] = useState(0.6);
  const [tieBreak, setTieBreak] = useState<MatchScoreTieBreak>('after');
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (seeded || !savedSettings.data) return;
    const fairness = savedSettings.data.prioritization.strategies.find((x) => x.key === 'fairness');
    setFairnessWeight(fairness?.weight ?? 0.6);
    setTieBreak(savedSettings.data.prioritization.matchScoreTieBreak);
    setSeeded(true);
  }, [savedSettings.data, seeded]);

  const prioritization: Prioritization = {
    ...DEFAULT_PRIORITIZATION,
    strategies: [
      { key: 'fairness', weight: fairnessWeight },
      { key: 'providerWeight', weight: Math.round((1 - fairnessWeight) * 100) / 100 },
    ],
    matchScoreTieBreak: tieBreak,
  };

  const savedFairness =
    savedSettings.data?.prioritization.strategies.find((x) => x.key === 'fairness')?.weight ?? 0.6;
  const isDirty =
    fairnessWeight !== savedFairness ||
    tieBreak !== (savedSettings.data?.prioritization.matchScoreTieBreak ?? 'after');

  function handleRun() {
    // Flush any uncommitted text in tag inputs and include in the request
    const pendingCpt = cptRef.current?.flush();
    const pendingIcd = icdRef.current?.flush();

    const finalCpt =
      pendingCpt && !cptCodes.includes(pendingCpt) ? [...cptCodes, pendingCpt] : cptCodes;
    const finalIcd =
      pendingIcd && !icdCodes.includes(pendingIcd) ? [...icdCodes, pendingIcd] : icdCodes;

    simulate.mutate({
      signals: {
        cptCodes: finalCpt,
        icdCodes: finalIcd,
        patientType: patientType === 'any' ? null : patientType,
        isUrgent,
        genderCode: null,
        authExpiresAt: null,
      },
      ...(providerId ? { providerId } : {}),
      ...(locationId ? { locationId } : {}),
      // Per-run only — the saved org policy is untouched unless "Save as org
      // default" is pressed.
      overrides: {
        prioritization: {
          strategies: prioritization.strategies,
          matchScoreTieBreak: tieBreak,
        },
      },
    });
  }

  function handleReset() {
    setCptCodes([]);
    setIcdCodes([]);
    setPatientType('any');
    setIsUrgent(false);
    setProviderId('');
    setLocationId('');
    // Weights are deliberately NOT reset — Reset clears the referral being
    // tested, not the algorithm configuration being evaluated across runs.
    simulate.reset();
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) handleReset();
    onOpenChange(nextOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="!max-w-4xl max-h-[85vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4" />
            Appointment Type Simulator
          </DialogTitle>
          <DialogDescription>
            Enter referral signals to see which appointment type the system would select and why.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left: Inputs */}
          <div className="w-72 shrink-0 border-r flex flex-col min-h-0">
            <ScrollArea className="flex-1 min-h-0">
              <div className="p-5 space-y-5">
                <TagInput
                  ref={cptRef}
                  label="CPT Codes (Procedure)"
                  tags={cptCodes}
                  onAdd={(t) => setCptCodes((prev) => [...prev, t])}
                  onRemove={(t) => setCptCodes((prev) => prev.filter((c) => c !== t))}
                  placeholder="e.g. 95810"
                />

                <TagInput
                  ref={icdRef}
                  label="ICD-10 Codes (Diagnosis)"
                  tags={icdCodes}
                  onAdd={(t) => setIcdCodes((prev) => [...prev, t])}
                  onRemove={(t) => setIcdCodes((prev) => prev.filter((c) => c !== t))}
                  placeholder="e.g. G47.33"
                />

                <div className="space-y-1.5">
                  <Label>Patient Type</Label>
                  <Select
                    value={patientType}
                    onValueChange={(v) => setPatientType(v as 'new' | 'existing' | 'any')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any / Unknown</SelectItem>
                      <SelectItem value="new">New patient</SelectItem>
                      <SelectItem value="existing">Existing patient</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Urgency</Label>
                  <Select
                    value={isUrgent ? 'urgent' : 'routine'}
                    onValueChange={(v) => setIsUrgent(v === 'urgent')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="routine">Routine</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Provider (optional)</Label>
                  <ProviderPicker
                    selected={providerId}
                    onChange={setProviderId}
                    placeholder="All providers"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Location (optional)</Label>
                  <LocationPicker
                    selected={locationId}
                    onChange={setLocationId}
                    placeholder="All locations"
                  />
                </div>

                <WeightsPanel
                  fairnessWeight={fairnessWeight}
                  onFairnessWeightChange={setFairnessWeight}
                  tieBreak={tieBreak}
                  onTieBreakChange={setTieBreak}
                  isDirty={isDirty}
                  isSaving={saveSettings.isPending}
                  onSaveDefault={() =>
                    saveSettings.mutate({
                      prioritization: {
                        strategies: prioritization.strategies,
                        matchScoreTieBreak: tieBreak,
                        tieBreak: 'provider_id',
                      },
                    })
                  }
                />
              </div>
            </ScrollArea>

            <div className="p-4 border-t space-y-2 shrink-0">
              <Button onClick={handleRun} disabled={simulate.isPending} className="w-full">
                {simulate.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Running...
                  </>
                ) : (
                  'Run simulation'
                )}
              </Button>
              {simulate.data && (
                <Button variant="ghost" onClick={handleReset} className="w-full text-xs">
                  Reset
                </Button>
              )}
            </div>
          </div>

          {/* Right: Results — w-0 forces flex item to not exceed allocated space */}
          <div className="flex-1 w-0">
            <ScrollArea className="h-full">
              <div className="p-5 max-w-full">
                {simulate.isPending && (
                  <div className="flex items-center justify-center h-40 text-muted-foreground">
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Scoring appointment types...
                  </div>
                )}

                {simulate.isError && (
                  <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    Something went wrong running the simulation.
                  </div>
                )}

                {simulate.isIdle && !simulate.data && (
                  <div className="flex flex-col items-center justify-center h-40 text-center text-muted-foreground space-y-2">
                    <FlaskConical className="h-8 w-8 opacity-30" />
                    <p className="text-sm">Enter referral signals and click "Run simulation"</p>
                  </div>
                )}

                {simulate.data && (
                  <AppointmentTypeResults result={simulate.data} prioritization={prioritization} />
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
